import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { faAngleLeft } from '@fortawesome/pro-regular-svg-icons';
import { Subscription } from 'rxjs';
import { KeyValue } from '@angular/common';
import { select, Store } from '@ngrx/store';
import { loadEditionsMetaData } from 'search/full-card/actions/editions.metadata.actions';
import { selectMetaData, selectMetaDataLoading } from 'search/full-card/reducers/editions.metadata.reducer';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { EditionMeta, EditionMetaData, EditionSourceType } from 'search/full-card/models/edition';
import { MarcViewerService } from '../../../../../services/marc-viewer.service';

@Component({
  selector: 'app-editions-sidebar',
  templateUrl: './editions-sidebar.component.html',
  styleUrls: ['./editions-sidebar.component.scss']
})
export class EditionsSidebarComponent implements OnInit, OnDestroy {
  private readonly subscriptions = new Subscription();
  public isLoading = true;
  public showFullMarcRecord = false;
  public fullMarcRecordAvailable = false;
  public edition: EditionMeta;
  public editionEntries: Array<KeyValue<string, unknown>> = [];
  public title: string;
  public marcRecord: HTMLElement;
  public source: EditionSourceType;
  public readonly closeFullMarc = faAngleLeft;

  public generalView: Array<KeyValue<string, unknown>>;
  public languagesView: Array<KeyValue<string, unknown>>;
  public accessibilityView: Array<KeyValue<string, unknown>>;
  public seriesView: Array<KeyValue<string, unknown>>;
  public swissCollectionsView: Array<KeyValue<string, unknown>>;
  public subjectsView: Array<KeyValue<string, unknown>>;
  public noteGeneralView: Array<KeyValue<string, unknown>>;
  public contributorsView: Array<KeyValue<string, unknown>>;
  public controlNumbersView: Array<KeyValue<string, unknown>>;
  @Input() public editionId: string;
  @Input() public rapido?: boolean = false;

  constructor(
    public store: Store,
    public cdRef: ChangeDetectorRef,
    private readonly ngbActiveModal: NgbActiveModal,
    private readonly marcViewerService: MarcViewerService
  ) {
  }

  public ngOnInit() {
    if (this.editionId) {
      this.store.dispatch(loadEditionsMetaData({id: this.editionId, rapido: this.rapido}));
      this.marcRecord = null;
    }

    this.subscriptions.add(
      this.store.pipe(select(selectMetaData)).subscribe((data: EditionMetaData) => {
        this.edition = data?.edition as EditionMeta;
        if (this.edition) {
          this.editionEntries = this.edition
            ? Object.entries(this.edition).map(([key, value]) => ({key, value}))
            : [];

          this.generalView = this.marcViewerService.groupMarcFields(this.editionEntries, 'general');

          const languages = this.marcViewerService.groupMarcFields(this.editionEntries, 'languages');
          this.languagesView = languages?.length && [{
            key: 'languageInformation',
            value: '',
            header: true
          }, ...languages];

          const accessibility = this.marcViewerService.groupMarcFields(this.editionEntries, 'accessibility');
          this.accessibilityView = accessibility?.length && [{key: 'accessibilityInfo', value: '', header: true},
            ...accessibility];

          const series = this.marcViewerService.groupMarcFields(this.editionEntries, 'series');

          this.seriesView = series?.length && [{
            key: 'seriesTitle',
            value: '',
            header: true,
          }, ...series];

          const swissCollections = this.marcViewerService.groupMarcFields(this.editionEntries, 'swissCollections');
          this.swissCollectionsView = swissCollections?.lenght && [{
            key: 'swissCollections',
            value: '',
            header: true,
          }, ...swissCollections];

          const subjects = this.marcViewerService.groupMarcFields(this.editionEntries, 'subjects');
          this.subjectsView = subjects?.length && [{
            key: 'subjects',
            value: '',
            header: true,
          }, ...subjects];

          const notes = this.marcViewerService.groupMarcFields(this.editionEntries, 'noteGeneral');
          this.noteGeneralView = notes?.length && [{
            key: 'noteTitle',
            value: '',
            header: true,
          }, ...notes];

          const contributors = this.marcViewerService.groupMarcFields(this.editionEntries, 'contributors');
          this.contributorsView = contributors?.length && [{
            key: 'contributors',
            value: '',
            header: true,
          }, ...contributors];

          const controlNumbers = this.marcViewerService.groupMarcFields(this.editionEntries, 'controlNumbers');
          this.controlNumbersView = controlNumbers?.length && [{
            key: 'controlNumbers',
            value: '',
            header: true,
          }, ...controlNumbers];
        }

        this.title = data?.edition?.title[0];
        this.source = data?.source as EditionSourceType;

        this.marcRecord = document.createElement('div');
        this.marcRecord.textContent = data?.marcRecord ? this.marcViewerService.marcJsonToView(data.marcRecord) : '';
        this.marcRecord.style.whiteSpace = 'pre-wrap'; // Preserve whitespace and line breaks
        this.marcRecord.style.fontFamily = 'monospace'; // Use a monospace font for better alignment

        this.fullMarcRecordAvailable = data?.source === EditionSourceType.MARC;
        this.cdRef.detectChanges();
      }),
    );

    this.subscriptions.add(
      this.store.pipe(select(selectMetaDataLoading)).subscribe((loading) => {
        this.isLoading = loading;
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public toggleFullMark(): void {
    this.showFullMarcRecord = !this.showFullMarcRecord;
    this.cdRef.detectChanges();
    // refresh the view after toggle
    setTimeout(() => {
      const container = document.getElementById('marc-container');
      if (container) {
        container.innerHTML = ''; // Clear previous content
        container.appendChild(this.marcRecord);
      }
      this.cdRef.detectChanges();
    }, 300);
  }

  public onDismissActiveModal() {
    this.ngbActiveModal.dismiss();
    this.marcRecord = null;
    this.cdRef.detectChanges();
  }
}
